import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { getAccessToken } from '../auth/accessToken';
import { useAuth } from '../auth/useAuth';
import { A2A_AGENT_ID, agentConfig } from '../config/agent-config';
import {
  buildMcpSnippets,
  buildProtocolExamples,
  buildSdkInstall,
  buildSdkUsage,
  PACKAGES,
  REPO_URL,
  type McpTab,
  type ProtocolTab,
} from './integrationsSnippets';
import './Integrations.css';
import { requestTimeout } from '../config/requestTimeout.ts';

// The agents API base (e.g. https://api.oculusmind.ai). `invokeUrl` is
// `${base}/agents`; the A2A + token surfaces live on the same domain.
const API_BASE = agentConfig.invokeUrl.replace(/\/agents$/, '');
// A2A is per-agent: `${base}/a2a/<agentId>` (one gateway child resource per
// agent). The agent is selected by the URL path, not the request body.
const A2A_URL = `${API_BASE}/a2a/${A2A_AGENT_ID}`;
const TOKEN_URL = `${API_BASE}/integrations/token`;

/** npm + GitHub destinations for one published package. */
function PackageLinks({ pkg }: { pkg: (typeof PACKAGES)[keyof typeof PACKAGES] }) {
  return (
    <p className="om-integrations__links">
      <a href={pkg.npmUrl} target="_blank" rel="noreferrer noopener">
        {pkg.name} on npm ↗
      </a>
      <a href={pkg.sourceUrl} target="_blank" rel="noreferrer noopener">
        Source ↗
      </a>
    </p>
  );
}

export default function Integrations() {
  const { status } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState<ProtocolTab>('http');
  const [mcpTab, setMcpTab] = useState<McpTab>('claude');

  const fetchToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = await getAccessToken();
      const res = await fetch(TOKEN_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: requestTimeout(),
  });
      if (!res.ok) {
        throw new Error(`Token request failed (${String(res.status)})`);
      }
      const body = (await res.json()) as { token: string; expiresAt: string };
      setToken(body.token);
      setExpiresAt(body.expiresAt);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const copy = useCallback(() => {
    if (token !== null) {
      void navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [token]);

  const effectiveToken = token ?? '<YOUR_TOKEN>';
  const examples = buildProtocolExamples(A2A_URL, effectiveToken);
  const mcpSnippets = buildMcpSnippets(effectiveToken, API_BASE);

  return (
    <article className="om-integrations">
      <Link to="/" className="om-integrations__home">
        ← OculusMind.AI
      </Link>
      <header className="om-integrations__header">
        <h1>Integrations</h1>
        <p>
          Reach your OculusMind.AI agents from Claude Code, from your own code, or straight over the
          open <strong>A2A</strong> (Agent-to-Agent) protocol.
        </p>
      </header>

      <section className="om-integrations__section">
        <h2 className="om-integrations__h2">Client integrations — calling your agents</h2>
        <p className="om-integrations__muted">
          Three ways in, easiest first: add the <strong>MCP server</strong> to Claude Code, install
          the <strong>TypeScript SDK</strong>, or call the <strong>raw A2A protocol</strong> from
          any language. All three use the same token.
        </p>

        {status === 'authenticated' ? (
          <div className="om-integrations__panel">
            <h3>Your A2A access token</h3>
            <p className="om-integrations__muted">
              A 7-day bearer token that authorizes A2A calls to your agents. Treat it like a
              password — anyone with it can invoke your agents (against your daily usage limit).
            </p>
            {token === null ? (
              <button
                type="button"
                className="om-integrations__btn"
                onClick={() => void fetchToken()}
                disabled={loading}
              >
                {loading ? 'Generating…' : 'Generate token'}
              </button>
            ) : (
              <>
                <div className="om-integrations__token">
                  <code data-testid="a2a-token">{token}</code>
                  <button
                    type="button"
                    className="om-integrations__btn om-integrations__btn--small"
                    onClick={copy}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="om-integrations__muted">
                  Expires {expiresAt}.{' '}
                  <button
                    type="button"
                    className="om-integrations__link"
                    onClick={() => void fetchToken()}
                  >
                    Regenerate
                  </button>
                </p>
              </>
            )}
            {error !== null && <p className="om-integrations__error">{error}</p>}
          </div>
        ) : status === 'unauthenticated' ? (
          <div className="om-integrations__panel" data-testid="signed-out-cta">
            <h3>Sign in to get a token</h3>
            <p className="om-integrations__muted">
              You need an OculusMind.AI account to generate an A2A token.
            </p>
            <div className="om-integrations__cta">
              <Link className="om-integrations__btn" to="/auth/signin">
                Sign in
              </Link>
              <Link className="om-integrations__btn om-integrations__btn--ghost" to="/auth/signup">
                Sign up
              </Link>
            </div>
          </div>
        ) : null}

        <div className="om-integrations__panel">
          <h3>Claude Code &amp; other MCP hosts</h3>
          <p className="om-integrations__muted">
            Run the <code>{PACKAGES.mcp.name}</code> server and your agents become tools any MCP
            host can call — no code to write. They appear as <code>list_agents</code> and{' '}
            <code>ask_agent</code>.
          </p>
          <div className="om-integrations__tabs" role="tablist">
            {(['claude', 'json'] as McpTab[]).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={mcpTab === t}
                className={`om-integrations__tab${mcpTab === t ? ' om-integrations__tab--active' : ''}`}
                onClick={() => setMcpTab(t)}
              >
                {t === 'claude' ? 'Claude Code' : 'Other MCP hosts'}
              </button>
            ))}
          </div>
          <pre className="om-integrations__code" data-testid="mcp-code">
            <code>{mcpSnippets[mcpTab]}</code>
          </pre>
          <PackageLinks pkg={PACKAGES.mcp} />
        </div>

        <div className="om-integrations__panel">
          <h3>TypeScript SDK</h3>
          <p className="om-integrations__muted">
            A dependency-free client for Node 18+, Bun, Deno, and browsers. Handles auth, retries,
            and streaming so you don&apos;t hand-roll the protocol.
          </p>
          <pre className="om-integrations__code" data-testid="sdk-install">
            <code>{buildSdkInstall()}</code>
          </pre>
          <pre className="om-integrations__code" data-testid="sdk-code">
            <code>{buildSdkUsage(effectiveToken, API_BASE, A2A_AGENT_ID)}</code>
          </pre>
          <PackageLinks pkg={PACKAGES.sdk} />
        </div>

        <div className="om-integrations__panel">
          <h3>Make a call — raw A2A</h3>
          <p className="om-integrations__muted">
            No client for your language? POST a JSON-RPC <code>message/send</code> to{' '}
            <code>{A2A_URL}</code> with your token.
          </p>
          <div className="om-integrations__tabs" role="tablist">
            {(['http', 'curl', 'ts'] as ProtocolTab[]).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`om-integrations__tab${tab === t ? ' om-integrations__tab--active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'http' ? 'Raw HTTP' : t === 'curl' ? 'curl' : 'TypeScript'}
              </button>
            ))}
          </div>
          <pre className="om-integrations__code" data-testid="example-code">
            <code>{examples[tab]}</code>
          </pre>
        </div>
      </section>

      <section className="om-integrations__section">
        <h2 className="om-integrations__h2">Service integrations — your agents calling out</h2>
        <p className="om-integrations__muted">
          Letting OculusMind.AI agents call your external services and tools is coming soon.
        </p>
      </section>

      <section className="om-integrations__section">
        <h2 className="om-integrations__h2">Open source, licensing &amp; pricing</h2>
        <p className="om-integrations__muted">
          The clients and the MCP server are Apache-2.0 and developed in the open at{' '}
          <a href={REPO_URL} target="_blank" rel="noreferrer noopener" data-testid="repo-link">
            OculusMindAI/oculusmind-agents-sdk ↗
          </a>{' '}
          — issues and pull requests welcome.
        </p>
        <p className="om-integrations__muted">
          Review the <a href="/license.txt">license</a> governing use of the A2A API and the
          Services, and see <Link to="/pricing">pricing</Link> for current tiers.
        </p>
      </section>
    </article>
  );
}
